import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Send, UserCheck, UserMinus, ChevronDown } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { notify } from '../lib/notify';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import { formatDateTime, formatRelative } from '../lib/format';
import type { Ticket, TicketStatus, TicketPriority } from '@vipsoft/shared-types';

interface TicketDetail extends Ticket {
  customer: {
    id: number;
    name: string;
    email: string | null;
    phone: string | null;
    source_platform: string;
  };
  assignee: { id: number; name: string; email: string; role: string } | null;
  messages: Array<{
    id: number;
    author_type: 'agent' | 'customer' | 'system';
    author_id: number | null;
    body: string;
    is_internal: boolean;
    created_at: string;
  }>;
  events: Array<{
    id: number;
    type: string;
    actor_type: 'agent' | 'customer' | 'system';
    payload: Record<string, unknown> | null;
    created_at: string;
  }>;
}

interface Assignable {
  id: number;
  name: string;
  email: string;
  role: 'agent' | 'supervisor';
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Nuevo',
  open: 'Abierto',
  in_progress: 'En proceso',
  waiting_customer: 'Esperando cliente',
  resolved: 'Resuelto',
  closed: 'Cerrado',
  reopened: 'Reabierto',
};

const ALL_STATUSES: TicketStatus[] = [
  'new',
  'open',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
  'reopened',
];

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baja',
};

const ALL_PRIORITIES: TicketPriority[] = ['urgent', 'high', 'normal', 'low'];

// Estados "irreversibles" en el sentido operativo — el tono del modal sube a danger.
const DESTRUCTIVE_STATUSES: TicketStatus[] = ['resolved', 'closed'];

export default function TicketDetail() {
  const { idOrCode } = useParams<{ idOrCode: string }>();
  const navigate = useNavigate();
  const myRole = useAuthStore((s) => s.user?.role);
  const canAssign = myRole === 'admin' || myRole === 'supervisor';

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  // Solo aplica si ticket.channel === 'whatsapp'. Default: group (todos lo ven).
  const [whatsappDestination, setWhatsappDestination] = useState<'group' | 'private'>('group');

  // Confirmaciones pendientes
  const [pendingStatus, setPendingStatus] = useState<TicketStatus | null>(null);
  const [pendingPriority, setPendingPriority] = useState<TicketPriority | null>(null);

  // Asignación
  const [assignOpen, setAssignOpen] = useState(false);

  const load = async () => {
    if (!idOrCode) return;
    setLoading(true);
    try {
      const data = await apiFetch<TicketDetail>(`/tickets/${idOrCode}`);
      setTicket(data);
    } catch (err) {
      notify.apiError(err, 'No se pudo cargar el ticket.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idOrCode]);

  const handleReply = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!reply.trim() || !ticket) return;
    setSending(true);
    try {
      const body: Record<string, unknown> = {
        body: reply.trim(),
        is_internal: isInternal,
      };
      if (ticket.channel === 'whatsapp' && !isInternal) {
        body.whatsapp_destination = whatsappDestination;
      }
      const updated = await apiFetch<TicketDetail>(`/tickets/${ticket.id}/messages`, {
        method: 'POST',
        body,
      });
      setTicket(updated);
      setReply('');
      if (isInternal) {
        notify.success('Nota interna agregada.');
      } else if (ticket.channel === 'whatsapp') {
        notify.success(
          whatsappDestination === 'private'
            ? 'Respuesta enviada al cliente (privado).'
            : 'Respuesta enviada al grupo.',
        );
      } else {
        notify.success('Respuesta enviada al cliente.');
      }
    } catch (err) {
      notify.apiError(err, 'No se pudo enviar la respuesta.');
    } finally {
      setSending(false);
    }
  };

  const doStatusChange = async () => {
    if (!ticket || pendingStatus === null) return;
    try {
      const updated = await apiFetch<TicketDetail>(`/tickets/${ticket.id}`, {
        method: 'PATCH',
        body: { status: pendingStatus },
      });
      setTicket(updated);
      const newStatus = pendingStatus;
      notify.success(`Estado cambiado a "${STATUS_LABEL[newStatus]}".`);
      // Al cerrar un ticket, devolvemos a la cola — el agente típicamente
      // sigue con el próximo ticket activo desde ahí.
      if (newStatus === 'closed') {
        navigate('/admin/tickets');
      }
    } catch (err) {
      notify.apiError(err, 'No se pudo cambiar el estado.');
      throw err;
    }
  };

  const doPriorityChange = async () => {
    if (!ticket || pendingPriority === null) return;
    try {
      const updated = await apiFetch<TicketDetail>(`/tickets/${ticket.id}`, {
        method: 'PATCH',
        body: { priority: pendingPriority },
      });
      setTicket(updated);
      notify.success(`Prioridad cambiada a "${PRIORITY_LABEL[pendingPriority]}".`);
    } catch (err) {
      notify.apiError(err, 'No se pudo cambiar la prioridad.');
      throw err;
    }
  };

  const onAssigned = (updated: TicketDetail, assigneeName: string | null) => {
    setTicket(updated);
    setAssignOpen(false);
    notify.success(
      assigneeName ? `Ticket asignado a ${assigneeName}.` : 'Asignación removida.',
    );
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-500 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin" />
        Cargando ticket…
      </div>
    );
  }

  if (!ticket) {
    return <div className="py-10 text-center text-rose-600">No se pudo cargar el ticket.</div>;
  }

  return (
    <>
      <Link
        to="/admin/tickets"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={14} />
        Volver a tickets
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-slate-900">
                  {ticket.subject}
                </h1>
                <div className="text-xs text-slate-500 mt-1 font-mono">{ticket.code}</div>
              </div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {ticket.description}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">
              Conversación ({ticket.messages.length})
            </div>
            <div className="divide-y divide-slate-100">
              {ticket.messages.map((m) => (
                <div
                  key={m.id}
                  className={`px-5 py-4 ${m.is_internal ? 'bg-amber-50/50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      <span
                        className={`font-medium ${
                          m.author_type === 'agent'
                            ? 'text-blue-700'
                            : m.author_type === 'customer'
                              ? 'text-slate-900'
                              : 'text-slate-500'
                        }`}
                      >
                        {m.author_type === 'agent'
                          ? 'Agente'
                          : m.author_type === 'customer'
                            ? ticket.customer.name
                            : 'Sistema'}
                      </span>
                      {m.is_internal && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          Nota interna
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {formatRelative(m.created_at)}
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {m.body}
                  </p>
                </div>
              ))}
            </div>

            <form onSubmit={handleReply} className="p-4 border-t border-slate-100 bg-slate-50/50">
              {ticket.channel === 'whatsapp' && !isInternal && (
                <div className="mb-3 flex items-center gap-3 text-xs">
                  <span className="text-slate-500">Enviar:</span>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="wa-destination"
                      value="group"
                      checked={whatsappDestination === 'group'}
                      onChange={() => setWhatsappDestination('group')}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-slate-700">Al grupo (todos lo verán)</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="wa-destination"
                      value="private"
                      checked={whatsappDestination === 'private'}
                      onChange={() => setWhatsappDestination('private')}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-slate-700">Privado al cliente</span>
                  </label>
                </div>
              )}
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={
                  isInternal
                    ? 'Escribí una nota privada para el equipo…'
                    : 'Escribí tu respuesta al cliente…'
                }
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  />
                  Nota interna (no visible al cliente)
                </label>
                <button
                  type="submit"
                  disabled={!reply.trim() || sending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  Enviar
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Detalles
            </h3>
            <dl className="space-y-2.5 text-sm">
              <SidebarRow
                label="Estado"
                value={
                  <select
                    value={ticket.status}
                    onChange={(e) => {
                      const next = e.target.value as TicketStatus;
                      if (next !== ticket.status) setPendingStatus(next);
                    }}
                    className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                  >
                    {ALL_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                }
              />
              <SidebarRow
                label="Prioridad"
                value={
                  <select
                    value={ticket.priority}
                    onChange={(e) => {
                      const next = e.target.value as TicketPriority;
                      if (next !== ticket.priority) setPendingPriority(next);
                    }}
                    className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                  >
                    {ALL_PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABEL[p]}
                      </option>
                    ))}
                  </select>
                }
              />
              <SidebarRow label="Canal" value={ticket.channel} />
              {ticket.platform && <SidebarRow label="Plataforma" value={ticket.platform} />}
              <SidebarRow label="Creado" value={formatDateTime(ticket.created_at)} />
              <SidebarRow
                label="SLA vence"
                value={ticket.sla_due_at ? formatDateTime(ticket.sla_due_at) : '—'}
              />
              {ticket.first_response_at && (
                <SidebarRow
                  label="1ª respuesta"
                  value={formatDateTime(ticket.first_response_at)}
                />
              )}
            </dl>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Asignación
              </h3>
              {canAssign && (
                <button
                  onClick={() => setAssignOpen(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                >
                  {ticket.assignee ? 'Cambiar' : 'Asignar'}
                  <ChevronDown size={12} />
                </button>
              )}
            </div>
            {ticket.assignee ? (
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-semibold grid place-items-center shrink-0">
                  {initialsOf(ticket.assignee.name)}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {ticket.assignee.name}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {ticket.assignee.email}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500 flex items-center gap-2">
                <UserMinus size={14} className="text-slate-400" />
                Sin asignar
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Cliente
            </h3>
            <div className="space-y-1.5 text-sm">
              <div className="font-medium text-slate-900">{ticket.customer.name}</div>
              {ticket.customer.email && (
                <div className="text-slate-600 text-xs">{ticket.customer.email}</div>
              )}
              {ticket.customer.phone && (
                <div className="text-slate-600 text-xs">{ticket.customer.phone}</div>
              )}
              <div className="text-[11px] text-slate-400 capitalize pt-1">
                Origen: {ticket.customer.source_platform}
              </div>
            </div>
          </div>

          {ticket.events.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Historial ({ticket.events.length})
              </h3>
              <ol className="space-y-3 text-xs">
                {ticket.events.map((e) => (
                  <li key={e.id} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-700">{eventLabel(e)}</div>
                      <div className="text-[10px] text-slate-400">
                        {formatRelative(e.created_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingStatus !== null}
        onOpenChange={(o) => !o && setPendingStatus(null)}
        title="Cambiar estado del ticket"
        description={
          pendingStatus && ticket && (
            <>
              El estado pasará de{' '}
              <strong className="text-slate-900">{STATUS_LABEL[ticket.status]}</strong> a{' '}
              <strong className="text-slate-900">{STATUS_LABEL[pendingStatus]}</strong>.
              {DESTRUCTIVE_STATUSES.includes(pendingStatus) && (
                <span className="block mt-2 text-xs text-amber-700">
                  Este cambio se considera de cierre operativo y queda registrado en el historial.
                </span>
              )}
            </>
          )
        }
        confirmLabel="Confirmar cambio"
        tone={pendingStatus && DESTRUCTIVE_STATUSES.includes(pendingStatus) ? 'warning' : 'default'}
        onConfirm={doStatusChange}
      />

      <ConfirmDialog
        open={pendingPriority !== null}
        onOpenChange={(o) => !o && setPendingPriority(null)}
        title="Cambiar prioridad"
        description={
          pendingPriority && ticket && (
            <>
              La prioridad pasará de{' '}
              <strong className="text-slate-900">{PRIORITY_LABEL[ticket.priority]}</strong> a{' '}
              <strong className="text-slate-900">{PRIORITY_LABEL[pendingPriority]}</strong>. Esto
              puede recalcular el SLA esperado.
            </>
          )
        }
        confirmLabel="Confirmar cambio"
        tone="default"
        onConfirm={doPriorityChange}
      />

      {canAssign && ticket && (
        <AssignModal
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          ticket={ticket}
          onAssigned={onAssigned}
        />
      )}
    </>
  );
}

function SidebarRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-900 text-right">{value}</dd>
    </div>
  );
}

function initialsOf(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .filter(Boolean)
    .join('');
}

function eventLabel(e: { type: string; payload: Record<string, unknown> | null }): string {
  switch (e.type) {
    case 'created':
      return 'Ticket creado';
    case 'status_changed': {
      const from = (e.payload?.from as string) ?? '?';
      const to = (e.payload?.to as string) ?? '?';
      return `Estado cambió de ${from} a ${to}`;
    }
    case 'priority_changed':
      return `Prioridad cambió`;
    case 'assigned':
      return 'Ticket asignado';
    case 'unassigned':
      return 'Asignación removida';
    case 'message_added':
      return e.payload?.is_internal ? 'Nota interna agregada' : 'Mensaje agregado';
    case 'sla_at_risk':
      return 'SLA en riesgo';
    case 'sla_breached':
      return 'SLA incumplido';
    default:
      return e.type;
  }
}

// ---------- AssignModal ----------

function AssignModal({
  open,
  onClose,
  ticket,
  onAssigned,
}: {
  open: boolean;
  onClose: () => void;
  ticket: TicketDetail;
  onAssigned: (updated: TicketDetail, assigneeName: string | null) => void;
}) {
  const [candidates, setCandidates] = useState<Assignable[] | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || candidates !== null) return;
    setLoadingCandidates(true);
    apiFetch<Assignable[]>('/users/assignable')
      .then((list) => setCandidates(list))
      .catch((err) => notify.apiError(err, 'No se pudo cargar la lista de agentes.'))
      .finally(() => setLoadingCandidates(false));
  }, [open, candidates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !candidates) return candidates ?? [];
    return candidates.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
    );
  }, [search, candidates]);

  const handleAssign = async (assigneeId: number | null, displayName: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await apiFetch<TicketDetail>(`/tickets/${ticket.id}/assign`, {
        method: 'POST',
        body: { assignee_id: assigneeId },
      });
      onAssigned(updated, displayName);
    } catch (err) {
      notify.apiError(err, 'No se pudo asignar el ticket.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !busy && onClose()}
      title="Asignar ticket"
      description={`Elegí un agente o supervisor para ${ticket.code}.`}
    >
      <div className="space-y-3">
        <input
          type="search"
          placeholder="Buscar por nombre o correo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {ticket.assignee && (
          <button
            onClick={() => handleAssign(null, null)}
            disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 rounded-lg disabled:opacity-40"
          >
            <UserMinus size={14} />
            Quitar asignación de {ticket.assignee.name}
          </button>
        )}

        <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
          {loadingCandidates && (
            <div className="px-3 py-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Cargando…
            </div>
          )}
          {!loadingCandidates && filtered.length === 0 && (
            <div className="px-3 py-6 text-sm text-slate-500 text-center">
              No hay agentes que coincidan.
            </div>
          )}
          {!loadingCandidates &&
            filtered.map((c) => {
              const isCurrent = ticket.assignee?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => !isCurrent && handleAssign(c.id, c.name)}
                  disabled={busy || isCurrent}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50 ${
                    isCurrent ? 'bg-blue-50/40 cursor-default' : ''
                  }`}
                >
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-[10px] font-semibold grid place-items-center shrink-0">
                    {initialsOf(c.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {c.name}
                      {c.role === 'supervisor' && (
                        <span className="ml-1.5 text-[10px] font-normal text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                          Supervisor
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                  </div>
                  {isCurrent && <UserCheck size={14} className="text-blue-600 shrink-0" />}
                </button>
              );
            })}
        </div>
      </div>
    </Modal>
  );
}
